import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from '../../config/api';

/**
 * Fetches a paginated product list for a filter set.
 *
 * Two behaviours changed from the original:
 *  - the fetch is driven by the hook itself, so callers no longer need
 *    an effect that fired twice on mount (once for the initial filter
 *    object, once for the query-string sync);
 *  - responses are sequenced, so a slow earlier request can no longer
 *    overwrite the results of a newer one.
 */
const useProducts = (filters = {}) => {
  const [products, setProducts] = useState([]);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [load, setLoad] = useState(true);
  const [err, setErr] = useState(null);

  const requestIdRef = useRef(0);
  const abortRef = useRef(null);

  // Filters arrive as a fresh object literal on every render, so
  // compare by value rather than by reference.
  const filtersKey = JSON.stringify(filters);

  const fetchproducts = useCallback(
    async (pageNumber = 1) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoad(true);

      try {
        const response = await fetch(apiUrl(`/api/products/fetch?page=${pageNumber}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: filtersKey,
          signal: controller.signal,
        });

        if (!response.ok) throw new Error('Network response was not ok');

        const data = await response.json();

        // Ignore anything that is no longer the newest request.
        if (requestId !== requestIdRef.current) return;

        setProducts(Array.isArray(data.data) ? data.data : []);
        setLastPage(data.last_page || 1);
        setCurrentPage(data.current_page || 1);
        setErr(null);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (requestId !== requestIdRef.current) return;
        setErr(error.message);
        setProducts([]);
      } finally {
        if (requestId === requestIdRef.current) setLoad(false);
      }
    },
    [filtersKey]
  );

  useEffect(() => {
    fetchproducts(1);
    return () => abortRef.current?.abort();
  }, [fetchproducts]);

  return { products, currentPage, lastPage, load, err, fetchproducts };
};

export default useProducts;
